import { modules } from '../constants';

/**
 * Create a new container for the notification banner.
 * Prepend it to the body so that it sits above all content.
 * @category Initialization
 * @subcategory Container creators
 * @module createNotificationBarContainer
 */
const createNotificationBarContainer = (): void => {
  const el = document.createElement('div');
  el.id = 'notification-banner-root';
  el.classList.add('react-module');
  document.body.prepend(el);
};

const createToastContainer = (): void => {
  const el = document.createElement('div');
  el.id = 'toast-container-root';
  el.classList.add('react-module');
  document.body.prepend(el);
};

const createAccountingContainer = (): void => {
  const container = document.getElementById('content');
  if (container) {
    container.innerHTML = '';
    container.classList.add('react-module');
    container.id = 'digit-react-content';
  }
};
const createEmailsContainer = (): void => {
  const container = document.getElementById('content');
  if (container) {
    container.innerHTML = '';
    container.classList.add('react-module');
    container.id = 'emails-react-content';
  }
};

const createUsersContainer = (): void => {
  const container = document.getElementsByTagName('section')[0];
  if (container) {
    container.innerHTML = '';
    container.classList.add('react-module');
    container.id = 'users-react-content';
  }
};

const createBrandingContainer = (): void => {
  const container = document.getElementsByTagName('section')[0];
  if (container) {
    container.innerHTML = '';
    container.classList.add('react-module');
    container.id = 'branding-react-content';
  }
};

const createSitesContainer = (): void => {
  const container = document.getElementsByTagName('section')[0];
  if (container) {
    container.innerHTML = '';
    container.classList.add('react-module');
    container.id = 'sites-react-content';
  }
};

const createQuestionsEditorContainer = (): void => {
  const container = document.getElementById('content');
  if (container) {
    container.innerHTML = '';
    container.classList.add('react-module');
    container.id = 'questions-editor-react-content';
  }
};
const createCookiesBarContainer = (): void => {
  const el = document.createElement('div');
  const oldBar = document.querySelector('.cc-banner');

  el.id = 'cookies-bar-react-content';
  el.classList.add('react-module');
  document.body.prepend(el);

  oldBar?.parentNode?.removeChild(oldBar);
};
const createImportMappingContainer = (): void => {
  const container = document.getElementById('content');
  if (container) {
    container.innerHTML = '';
    container.classList.add('react-module');
    container.id = 'import-mapping-react-content';
  }
};
const createPermissionsContainer = (): void => {
  if (process.env.NODE_ENV !== 'development') {
    const container = document.getElementById(
      'ctl00_MainContent_divPermissions',
    );
    if (container) {
      container.style.width = '100%';
    }
  }
};
const createTranslationsEditorContainer = (): void => {
  const isADMTranslationsUrl =
    window.location.href.toLowerCase().indexOf('/adm/translations.aspx') !== -1;

  const isStandardTranslationsUrl =
    window.location.href
      .toLowerCase()
      .indexOf('/settings/translations.aspx') !== -1;

  if (isADMTranslationsUrl) {
    const container = document.getElementById(
      'ctl00_ControlMessages_MessagingContainer',
    );
    if (container) {
      container.innerHTML = '';
      container.classList.remove('MessagingContainer');
      container.classList.add('react-module');
      container.id = 'translations-editor-react-content';
    }
  }

  if (isStandardTranslationsUrl) {
    const container = document.getElementsByClassName(
      'formOuterGenericEdit',
    )[0];
    container.innerHTML = '';
    container.classList.remove('formOuterGenericEdit');
    container.classList.add('react-module');
    container.id = 'translations-editor-react-content';
  }
};

const createFileLookupsContainer = (): void => {
  const container = document.getElementById('content');
  if (container) {
    container.innerHTML = '';
    container.classList.add('react-module');
    container.id = 'file-lookups-react-content';
  }
};

const createEmailTemplatesContainer = (): void => {
  const container = document.getElementById('content');
  if (container) {
    container.innerHTML = '';
    container.classList.add('react-module');
    container.id = 'email-templates-react-content';
  }
};

const createTrackingContainer = (): void => {
  const el = document.createElement('div');
  el.id = 'tracking-container-root';
  el.classList.add('react-module');
  document.body.prepend(el);
};

const createChatContainer = (): void => {
  const el = document.createElement('div');
  el.id = 'chat-container-root';
  el.classList.add('react-module');
  document.body.prepend(el);
};

/**
 * Style the container for the Machine Keys module.
 * @category Initialization
 * @subcategory Container creators
 * @module createMachineKeysContainer
 */
const createMachineKeysContainer = (): void => {
  const container = document.getElementById('ss_tabcontent');
  if (container) {
    container.classList.add('react-module', 'bg-gray-800');
  }
};

export const containerCreators = {
  [modules.NOTIF_BANNER]: {
    create: createNotificationBarContainer,
    root: 'notification-banner-root',
  },
  [modules.TOAST_CONTAINER]: {
    create: createToastContainer,
    root: 'toast-container-root',
  },
  [modules.ACCOUNTING]: {
    create: createAccountingContainer,
    root: 'digit-react-content',
  },
  [modules.EMAILS]: {
    create: createEmailsContainer,
    root: 'emails-react-content',
  },
  [modules.TRANSLATIONS_EDITOR]: {
    create: createTranslationsEditorContainer,
    root: 'translations-editor-react-content',
  },
  [modules.PERMISSIONS]: {
    create: createPermissionsContainer,
    root: 'permissions-react-content',
  },
  [modules.MACHINE_KEYS]: {
    create: createMachineKeysContainer,
    root: 'machine-keys-react-content',
  },
  [modules.QUESTIONS_EDITOR]: {
    create: createQuestionsEditorContainer,
    root: 'questions-editor-react-content',
  },
  [modules.COOKIES_BAR]: {
    create: createCookiesBarContainer,
    root: 'cookies-bar-react-content',
  },
  [modules.IMPORT_MAPPING]: {
    create: createImportMappingContainer,
    root: 'import-mapping-react-content',
  },
  [modules.FILE_LOOKUPS]: {
    create: createFileLookupsContainer,
    root: 'file-lookups-react-content',
  },
  [modules.EMAIL_TEMPLATES]: {
    create: createEmailTemplatesContainer,
    root: 'email-templates-react-content',
  },
  [modules.USERS]: {
    create: createUsersContainer,
    root: 'users-react-content',
  },
  [modules.SITES]: {
    create: createSitesContainer,
    root: 'sites-react-content',
  },
  [modules.TRACKING]: {
    create: createTrackingContainer,
    root: 'tracking-container-root',
  },
  [modules.CHAT]: {
    create: createChatContainer,
    root: 'chat-container-root',
  },
  [modules.BRANDING]: {
    create: createBrandingContainer,
    root: 'branding-react-content',
  },
};

export default containerCreators;
